import { useMemo, useState, useRef, useEffect } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrow-down.svg';
import { ReactComponent as CheckmarkIcon } from '../../../assets/icons/Check.svg';
import './MultiSelectSearch.scss';

interface SelectOption {
  key: any;
  value: any;
}

interface MultiSelectSearchProps {
  options: SelectOption[];
  selectedOptions: any[];
  onChange: (option: SelectOption, name: string) => void;
  placeholder?: string;
  name: string;
  isError: boolean;
  errorMessage?: string;
  disabled?: boolean;
  title?: string;
  maxHeight?: string;
}

const MultiSelectSearch: React.FC<MultiSelectSearchProps> = ({
  options,
  selectedOptions,
  onChange,
  placeholder,
  name,
  disabled,
  title,
  maxHeight
}) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isDropdownVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownVisible]);

  const onOptionSelect = (option: SelectOption) => {
    onChange(option, name);
  };

  const selectedOptionsValues = useMemo(
    () => selectedOptions.map((option) => option.value),
    [selectedOptions]
  );

  const filteredOptions = useMemo(() => {
    return options.filter(option => 
      option.value.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  return (
    <div className='multi-select-container2 input-wrapper'>
      <OutsideClickHandler
        onOutsideClick={() => {
          if (!disabled) setIsDropdownVisible(false);
        }}
      >
        {title && <div className='text-input-title '>{title}</div>}
        <div
          className={`select-input-container ${
            disabled ? 'disabled-select' : ''
          }`}
          onClick={() => {
            if (!disabled) setIsDropdownVisible((prev) => !prev);
          }}
        >
          
          <div className='selected-options-list2'>
            {selectedOptionsValues && selectedOptionsValues.length ? (
              <div className='selected-list'>
                {selectedOptionsValues.join(', ')}
              </div>
            ) : (
              <div className='placeholder'>{placeholder}</div>
            )}
          </div>
          <ArrowDownIcon
            className={`multi-select-arrow ${isDropdownVisible && 'rotated'}`}
          />
        </div>
        {isDropdownVisible && (
          <div className='dropdown-container' style={{ maxHeight }}>
            <div className='search-container'>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className='search-input'
              />
            </div>
            <div className='options-list'>
              {filteredOptions.map((option) => (
                <div
                  key={option.key}
                  className='option'
                  onClick={() => onOptionSelect(option)}
                >
                  <div className='option-value'>{option.value}</div>
                  {selectedOptions.some(
                    (selectedOption) => selectedOption.key === option.key
                  ) && <CheckmarkIcon className='checkmark' />}
                </div>
              ))}
              {filteredOptions.length === 0 && (
                <div className='no-options'>No options found</div>
              )}
            </div>
          </div>
        )}
      </OutsideClickHandler>
    </div>
  );
};

export default MultiSelectSearch;
